import { useEffect, useState, useCallback } from "react";
import { Box } from "@mui/material";
import { SLIDE_INTERVAL_MS, SPECS } from "./specsTheme";

export const useSlideController = (slides, intervalMs = SLIDE_INTERVAL_MS) => {
  const [index, setIndex] = useState(0);
  const count = slides?.length || 0;

  const next = useCallback(() => {
    if (count <= 1) return;
    setIndex((i) => (i + 1) % count);
  }, [count]);

  const goTo = useCallback(
    (i) => {
      if (count === 0) return;
      setIndex(((i % count) + count) % count);
    },
    [count]
  );

  useEffect(() => {
    if (count <= 1) return undefined;
    const id = setInterval(next, intervalMs);
    return () => clearInterval(id);
  }, [count, intervalMs, next]);

  return {
    index,
    slide: slides?.[index] ?? null,
    next,
    goTo,
    count,
  };
};

export const SlideDots = ({ slides, index, goTo }) => {
  if (!slides || slides.length <= 1) return null;
  return (
    <Box
      sx={{
        display: "flex",
        gap: 1,
        justifyContent: { xs: "center", md: "flex-start" },
      }}
    >
      {slides.map((s, i) => (
        <Box
          key={s.id || i}
          component="button"
          type="button"
          aria-label={`Go to slide ${i + 1}`}
          onClick={() => goTo(i)}
          sx={{
            width: i === index ? 28 : 10,
            height: 10,
            borderRadius: 999,
            border: "none",
            p: 0,
            cursor: "pointer",
            transition: "width 0.35s ease, background 0.35s ease",
            bgcolor: i === index ? SPECS.orange : "rgba(255,255,255,0.28)",
            boxShadow: i === index ? `0 0 12px ${SPECS.magenta}88` : "none",
          }}
        />
      ))}
    </Box>
  );
};

/**
 * Owns slide rotation. Pass a render function as children:
 * ({ slide, index, goTo, count }) => ...
 */
const SlideController = ({
  slides,
  intervalMs = SLIDE_INTERVAL_MS,
  children,
}) => {
  const controller = useSlideController(slides, intervalMs);
  return typeof children === "function" ? children(controller) : null;
};

export default SlideController;
